(() => {
  const POTENCY_ORDER = ['Q (Mother Tincture)', 'Q', 'LM', 'LM1', '3x', '6x', '12x', '30x', '200x', '6c', '12c', '30c', '200c', '1M', '10M', '50M', 'CM', 'MM'];
  const KNOWN_BASES = ['Globules', 'Liquid'];

  class MansooraMedicinePicker extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.potencyIdx = parseInt(this.dataset.potencyIndex, 10);
      this.baseIdx = parseInt(this.dataset.baseIndex, 10);
      this.sizeIdx = parseInt(this.dataset.sizeIndex, 10);

      const dataNode = this.querySelector('[data-mansoora-variants]');
      try {
        this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
      } catch (err) {
        console.error('[mansoora] failed to parse variants', err);
        this.variants = [];
      }

      this.variantInput = this.querySelector('[data-mansoora-variant-input]');
      const initialId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      this.selected = this.variants.find(v => v.id === initialId)
        || this.firstAvailableVariant()
        || this.variants[0];
      if (!this.selected) return;

      this.bindPotencyButtons();
      this.bindSizeButtons();
      this.syncFormVariant({ skipFetch: true });
    }

    firstAvailableVariant() {
      return this.variants.find(v => v.available);
    }

    bindPotencyButtons() {
      this.querySelectorAll('[data-mansoora-potency]').forEach(btn => {
        btn.addEventListener('click', () => this.onPotencyClick(btn));
      });
    }

    bindSizeButtons() {
      this.querySelectorAll('[data-mansoora-variant]').forEach(btn => {
        btn.addEventListener('click', () => this.onVariantClick(btn));
      });
    }

    onPotencyClick(btn) {
      const newPotency = btn.dataset.mansooraPotency;
      this.querySelectorAll('[data-mansoora-potency]').forEach(b => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      const candidate = this.variants.find(v =>
        v.options[this.potencyIdx] === newPotency &&
        v.options[this.baseIdx] === this.selected.options[this.baseIdx] &&
        v.options[this.sizeIdx] === this.selected.options[this.sizeIdx]
      ) || this.variants.find(v =>
        v.options[this.potencyIdx] === newPotency && v.available
      ) || this.variants.find(v => v.options[this.potencyIdx] === newPotency);

      if (!candidate) return;
      this.selected = candidate;
      this.renderGrid();
      this.renderSizeOnly();
      this.syncFormVariant();
    }

    onVariantClick(btn) {
      const variantId = parseInt(btn.dataset.mansooraVariant, 10);
      const found = this.variants.find(v => v.id === variantId);
      if (!found) return;
      this.selected = found;
      this.querySelectorAll('[data-mansoora-variant]').forEach(b => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      this.syncFormVariant();
    }

    renderGrid() {
      const grid = this.querySelector('[data-mansoora-grid]');
      if (!grid) return;
      const currentPotency = this.selected.options[this.potencyIdx];

      const html = KNOWN_BASES.map(base => {
        const variants = this.variants.filter(v =>
          v.options[this.potencyIdx] === currentPotency &&
          v.options[this.baseIdx] === base
        );
        if (!variants.length) return '';
        const sizes = variants.map(v => this.sizeButtonHtml(v)).join('');
        return `<div class="mansoora-picker__base-card" data-mansoora-base="${base}">` +
               `<p class="mansoora-picker__base-label">${base}</p>` +
               `<div class="mansoora-picker__sizes">${sizes}</div>` +
               `</div>`;
      }).join('');

      grid.innerHTML = html;
      this.bindSizeButtons();
    }

    renderSizeOnly() {
      const container = this.querySelector('[data-mansoora-size-only]');
      if (!container) return;
      const matchPrimary = this.potencyIdx >= 0;
      const currentPrimary = matchPrimary ? this.selected.options[this.potencyIdx] : null;

      const html = this.variants
        .filter(v => !matchPrimary || v.options[this.potencyIdx] === currentPrimary)
        .map(v => this.sizeButtonHtml(v))
        .join('');

      container.innerHTML = html;
      this.bindSizeButtons();
    }

    sizeButtonHtml(v) {
      const isSelected = v.id === this.selected.id;
      const cls = `mansoora-picker__size${v.available ? '' : ' is-unavailable'}`;
      const price = this.formatPrice(v.price);
      const label = (this.sizeIdx >= 0 ? v.options[this.sizeIdx] : v.options[this.potencyIdx] || '').toString().replace(/"/g, '&quot;');
      return `<button type="button" class="${cls}" aria-pressed="${isSelected}" data-mansoora-variant="${v.id}" data-mansoora-size="${label}">` +
             `<span class="mansoora-picker__size-name">${label}</span>` +
             `<span class="mansoora-picker__size-price">${price}</span>` +
             `</button>`;
    }

    syncFormVariant(opts = {}) {
      if (!this.selected) return;
      const variantId = this.selected.id;
      const variantIdStr = String(variantId);

      if (this.variantInput) {
        this.variantInput.value = variantId;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update ANY other `name="id"` input that belongs to a product form in the
      // same shopify-section. The buy-buttons block renders its own variant
      // input inside <product-form-component> and the form ID does NOT match
      // the picker's data-product-id — so scope by section, not by form ID.
      const section = this.closest('.shopify-section') || document;
      section.querySelectorAll('input[name="id"]').forEach(input => {
        if (input !== this.variantInput && input.value !== variantIdStr) {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (_) { /* noop */ }

      this.dispatchEvent(new CustomEvent('mansoora:variant-change', {
        bubbles: true,
        detail: { variant: this.selected }
      }));

      if (!opts.skipFetch) {
        this.fetchAndDispatchUpdate(variantId);
      }
    }

    fetchAndDispatchUpdate(variantId) {
      if (this._fetchAbort) this._fetchAbort.abort();
      this._fetchAbort = new AbortController();

      const baseUrl = (this.productUrl || window.location.pathname).split('?')[0];
      const requestUrl = `${baseUrl}?variant=${variantId}`;

      fetch(requestUrl, { signal: this._fetchAbort.signal })
        .then(res => res.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const event = new CustomEvent('variant:update', {
            bubbles: true,
            detail: {
              resource: this.selected,
              sourceId: String(variantId),
              data: { html: html, productId: this.productId, newProduct: undefined }
            }
          });
          this.dispatchEvent(event);
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.warn('[mansoora] section fetch failed', err);
          }
        });
    }

    formatPrice(cents) {
      const dollars = (cents / 100);
      const fmt = window.Shopify && window.Shopify.formatMoney
        ? window.Shopify.formatMoney(cents)
        : `$${dollars.toFixed(2)}`;
      return fmt;
    }
  }

  if (!customElements.get('mansoora-medicine-picker')) {
    customElements.define('mansoora-medicine-picker', MansooraMedicinePicker);
  }
})();
